<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
        <!-- List Tab -->
        <vs-tab label="DRP Transaction List">
            <div class="mt-6" style="height: calc(100vh - 300px); overflow-y: auto; overflow-x: hidden;">
                <!-- Action Buttons -->
                <vs-row class="mb-4">
                    <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                        <div class="flex justify-end gap-2">
                             <vs-button @click="activeTab = 1; resetForm();" color="primary" type="filled" class="mr-2">
                                 <font-awesome-icon :icon="plusIcon" class="mr-2" /> Add New
                             </vs-button>
                            <vx-tooltip text="Refresh Data" color="primary">
                                <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                    <font-awesome-icon :icon="redoIcon" />
                                </vs-button>
                            </vx-tooltip>
                        </div>
                    </vs-col>
                </vs-row>

                <!-- Search & Filter Row -->
                <vs-row class="mb-3">
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="px-0 pr-2 mb-2">
                        <vs-input
                            class="w-full"
                            placeholder="Search by PO number or remark..."
                            v-model="filters.search"
                        />
                    </vs-col>
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="px-0 pr-2 mb-2">
                        <vs-input
                            class="w-full"
                            type="date"
                            v-model="filters.filter_date"
                        />
                    </vs-col>
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="px-0 pr-2 mb-2">
                        <InputSelect
                            :model.sync="filters.filter_status"
                            label=""
                            placeholder="All Status"
                            container_class="w-full"
                            :options="statusFilterOptions"
                        />
                    </vs-col>
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="px-0 mb-2 flex items-end">
                        <vs-button v-if="has_active_filters" @click="clearFilters" color="warning" type="border" class="w-full">
                            Clear Filters
                        </vs-button>
                    </vs-col>
                </vs-row>

                <vx-card>
                    <div v-if="loading" class="text-center p-4">
                        <h3>Loading Data.. Please wait..</h3>
                    </div>

                    <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                        <vs-tr>
                            <vs-th style="min-width: 60px;">S.No</vs-th>
                            <vs-th style="min-width: 120px;">Transaction Date</vs-th>
                            <vs-th style="min-width: 120px;">PO Number</vs-th>
                            <vs-th style="min-width: 150px;">Item Sub Group</vs-th>
                            <vs-th style="min-width: 100px;">UOM</vs-th>
                            <vs-th style="min-width: 120px;">Total Value</vs-th>
                            <vs-th style="min-width: 100px;">Status</vs-th>
                            <vs-th style="min-width: 150px;">Remark</vs-th>
                            <vs-th style="min-width: 100px;">Action</vs-th>
                        </vs-tr>
                        <template v-if="results.length > 0">
                            <template v-for="(item, index) in results">
                                <vs-tr :key="index">
                                    <vs-td>{{ filters.offset + index + 1 }}</vs-td>
                                    <vs-td>{{ item.transaction_date || '-' }}</vs-td>
                                    <vs-td>{{ getPoNumber(item) }}</vs-td>
                                    <vs-td>{{ getItemSubGroupCode(item) }}</vs-td>
                                    <vs-td>{{ getUOMCode(item) }}</vs-td>
                                    <vs-td>{{ item.total_value || '-' }}</vs-td>
                                    <vs-td>
                                        <span :class="getStatusClass(item.status)">
                                            {{ item.status || 'draft' }}
                                        </span>
                                    </vs-td>
                                    <vs-td>{{ item.remark || '-' }}</vs-td>
                                    <vs-td>
                                        <div class="flex">
                                            <vx-tooltip text="View Params">
                                                <vs-button
                                                    @click="viewParams(item)"
                                                    color="primary"
                                                    type="flat"
                                                    class="p-2"
                                                >
                                                    <font-awesome-icon :icon="viewIcon" />
                                                </vs-button>
                                            </vx-tooltip>
                                            <vx-tooltip text="Edit Transaction">
                                                <vs-button
                                                    @click="editItem(item)"
                                                    color="warning"
                                                    type="flat"
                                                    class="p-2"
                                                >
                                                    <font-awesome-icon :icon="editIcon" />
                                                </vs-button>
                                            </vx-tooltip>
                                             <vx-tooltip text="Delete Transaction" v-if="isActuallyAdmin">
                                                 <vs-button
                                                     color="danger"
                                                     type="flat"
                                                     class="p-2"
                                                     @click="confirmDelete(item)"
                                                 >
                                                     <font-awesome-icon :icon="deleteIcon" />
                                                 </vs-button>
                                             </vx-tooltip>
                                        </div>
                                    </vs-td>
                                </vs-tr>
                            </template>
                        </template>
                        <template v-else>
                            <vs-tr>
                                <vs-td colspan="9" class="text-center p-4">
                                    <p class="text-muted mb-0">No data available</p>
                                </vs-td>
                            </vs-tr>
                        </template>
                    </vs-table>
                </vx-card>

                <!-- Pagination -->
                <template v-if="total_results">
                    <vs-row class="mt-3 text-center">
                        <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                    </vs-row>
                </template>
            </div>
        </vs-tab>

        <!-- Form Tab -->
        <vs-tab :label="editingId ? 'Edit DRP Transaction' : 'Add New Transaction'">
            <div class="mt-6" style="height: calc(100vh - 300px); overflow-y: auto; overflow-x: hidden;">
                <vx-card noShadow noRound>
                    <div class="mb-4">
                        <h3 class="mb-2">{{ editingId ? 'Edit DRP Transaction' : 'Create DRP Transaction' }}</h3>
                        <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new DRP transaction entry.</p>
                        <p class="text-muted mb-4" v-else>Update the DRP transaction below.</p>
                    </div>

                    <!-- Row 1: Transaction Date, PO No, Item Sub Group Code, UOM Code -->
                    <vs-row class="px-2">
                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">Transaction Date *</label>
                            <vs-input
                                v-model="formData.transaction_date"
                                type="date"
                                class="w-full mt-1"
                            />
                            <small class="block text-danger mt-1" v-if="api_errors.transaction_date">
                                {{ (api_errors.transaction_date || []).join(', ') }}
                            </small>
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">PO No *</label>
                            <SelectForeign
                                :model.sync="formData.po_number"
                                placeholder="Select PO..."
                                :multiple="false"
                                :load_initial="true"
                                :label_keys="['po_number']"
                                :show_id_in_label="false"
                                :endpoint="poEndpoint"
                                container_class="w-full mt-1"
                                input_class=""
                                :use_id_as_value="true"
                                :close_on_select="true"
                                :append_to_body="true"
                            />
                            <small class="block text-danger mt-1" v-if="api_errors.po_number">
                                {{ (api_errors.po_number || []).join(', ') }}
                            </small>
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">Item Sub Group Code *</label>
                            <v-select
                                v-model="formData.item_sub_group"
                                :options="poItemSubGroupOptions"
                                label="code"
                                :reduce="option => option.id"
                                :clearable="false"
                                :placeholder="formData.po_number ? 'Select Item Sub Group...' : 'Select PO first'"
                                class="w-full mt-1"
                                :disabled="!formData.po_number"
                                :append-to-body="true"
                            ></v-select>
                            <small class="block text-danger mt-1" v-if="api_errors.item_sub_group">
                                {{ (api_errors.item_sub_group || []).join(', ') }}
                            </small>
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">UOM Code</label>
                            <vs-input
                                v-model="formData.uom_code"
                                type="text"
                                placeholder="Auto-filled"
                                class="w-full mt-1"
                                disabled
                            />
                        </vs-col>
                    </vs-row>

                    <!-- Row 2: Unit Rate, Remark, Status, DRP Input Format -->
                    <vs-row class="px-2">
                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">Unit Rate</label>
                            <vs-input
                                v-model.number="formData.unit_rate"
                                type="number"
                                step="0.01"
                                placeholder="Auto-filled"
                                class="w-full mt-1"
                                disabled
                            />
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">Remarks</label>
                            <vs-input
                                v-model="formData.remark"
                                type="text"
                                placeholder="Enter remarks"
                                class="w-full mt-1"
                            />
                            <small class="block text-danger mt-1" v-if="api_errors.remark">
                                {{ (api_errors.remark || []).join(', ') }}
                            </small>
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">Status</label>
                            <vs-select
                                v-model="formData.status"
                                placeholder="Select Status"
                                class="w-full mt-1"
                                autocomplete
                                :disabled="!editingId || !canApprove"
                            >
                                <vs-select-item
                                    v-for="item in statusOptions"
                                    :key="item.value"
                                    :value="item.value"
                                    :text="item.text"
                                />
                            </vs-select>
                        </vs-col>

                        <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="p-3">
                            <label class="custom-label">DRP Input Format</label>
                            <vs-button
                                @click="openDRPDialog"
                                color="primary"
                                type="filled"
                                class="w-full mt-1 align-button"
                                :disabled="!selectedItemSubGroup || !hasDRPInputFormat"
                            >
                                <font-awesome-icon :icon="settingsIcon" class="mr-2" />
                                Fill DRP Input Format
                            </vs-button>
                            <small class="text-muted block mt-1" v-if="!selectedItemSubGroup">
                                Select Item Sub Group first
                            </small>
                        </vs-col>
                    </vs-row>

                    <vs-row class="mt-4">
                        <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                            <vs-button @click="activeTab = 0; resetForm();" color="warning" type="flat" class="mr-2">
                                 Cancel
                             </vs-button>
                            <vs-button @click="save" :disabled="submit_disabled || !isFormValid" color="primary" type="filled">
                                {{ editingId ? 'Update' : 'Save' }}
                            </vs-button>
                            <small v-if="!isFormValid" class="text-danger block mt-2">
                                Please fill all required fields and DRP Input Format values
                            </small>
                        </vs-col>
                    </vs-row>
                </vx-card>
            </div>
        </vs-tab>
    </vs-tabs>

            <!-- DRP Input Format Dialog (for creating/editing) -->
            <vs-popup width="650px" :active.sync="drpDialogActive" title="DRP Input Format" class="drp-input-format-popup">
                <div>
                    <div class="mb-4">
                        <h4 class="mb-3">Fill Values for DRP Input Format</h4>
                        <p class="text-muted mb-3">Keys are fetched from Item Sub Group. Please fill all values.</p>

                        <div class="drp-container">
                            <div class="drp-table">
                                <div class="drp-table-header">
                                    <div class="drp-cell key-cell">Key</div>
                                    <div class="drp-cell value-cell">Value</div>
                                </div>
                                <template v-if="sortedDrpKeys.length > 0">
                                    <div class="drp-table-row" v-for="key in sortedDrpKeys" :key="key">
                                        <div class="drp-cell key-cell">
                                            <span class="drp-key-text">{{ key }}</span>
                                        </div>
                                        <div class="drp-cell value-cell">
                                            <v-select
                                                v-if="key === 'side'"
                                                v-model="drpInputFormat[key]"
                                                :options="formattedSideOptions"
                                                :reduce="opt => opt.value"
                                                placeholder="Select Side"
                                                class="w-full v-select-drp"
                                            />
                                            <v-select
                                                v-else-if="key === 'lane'"
                                                v-model="drpInputFormat[key]"
                                                :options="formattedLaneOptions"
                                                :reduce="opt => opt.value"
                                                placeholder="Select Lane"
                                                class="w-full v-select-drp"
                                            />
                                            <input v-else v-model="drpInputFormat[key]" placeholder="Enter value" class="drp-input" />
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="drp-table-row">
                                    <div class="drp-cell" style="text-align: center; padding: 20px;">
                                        No DRP Input Format keys found for this Item Sub Group.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="text-right mt-3">
                        <vs-button @click="drpDialogActive = false" color="warning" type="flat" class="mr-2">Cancel</vs-button>
                        <vs-button @click="saveDRPInputFormat" color="primary" type="filled" :disabled="!allDRPFieldsFilled">Save</vs-button>
                        <div class="mt-1 h-4">
                            <small v-if="!allDRPFieldsFilled" class="text-danger block">Please fill all fields to enable Save button</small>
                        </div>
                    </div>
                </div>
            </vs-popup>

            <!-- View Params Dialog -->
            <vs-popup width="650px" :active.sync="viewParamsDialogActive" title="DRP Input Format Details" class="drp-input-format-popup">
                <div>
                    <div class="mb-4">
                        <h4 class="mb-3">DRP Input Format Values</h4>

                        <div class="drp-container">
                            <div class="drp-table">
                                <div class="drp-table-header">
                                    <div class="drp-cell key-cell">Key</div>
                                    <div class="drp-cell value-cell">Value</div>
                                </div>
                                <template v-if="viewParamsData && Object.keys(viewParamsData).length > 0">
                                    <div class="drp-table-row" v-for="key in sortedViewParamsKeys" :key="key">
                                        <div class="drp-cell key-cell">
                                            <span class="drp-key-text">{{ key }}</span>
                                        </div>
                                        <div class="drp-cell value-cell">
                                            <input :value="formatDrpValue(viewParamsData[key])" disabled class="drp-input disabled" />
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="drp-table-row">
                                    <div class="drp-cell" style="text-align: center; padding: 20px;">
                                        No DRP Input Format data available.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="text-right mt-4">
                        <vs-button @click="viewParamsDialogActive = false" color="primary" type="filled">Close</vs-button>
                    </div>
                </div>
            </vs-popup>
        </template>
    </div>
</template>

<script>
import { faRedo, faCog, faEye, faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';
import SelectForeign from '../../components/letstream/ls-select-foreign/SelectForeign.vue';

import vSelect from 'vue-select';

export default {
    components: {
        SelectForeign,
        vSelect
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;

        return {
            results: [],
            loading: false,
            submit_disabled: false,
            api_errors: {},
            editingId: null,
            total_results: null,
            current_page: current_page,
            filters: {
                offset: offset,
                limit: limit,
                search: this.$route.query.search || null,
                filter_date: this.$route.query.filter_date || null,
                filter_status: this.$route.query.filter_status || null
            },
            statusFilterOptions: [
                { value: null, label: 'All Status' },
                { value: 'draft', label: 'Draft' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' }
            ],
            formData: {
                transaction_date: null,
                po_number: null,
                item_sub_group: null,
                uom_code: '',
                unit_rate: null,
                remark: '',
                status: 'draft'
            },
            selectedPO: null,
            selectedItemSubGroup: null,
            drpDialogActive: false,
            drpInputFormat: {},
            drpKeys: [],
            sideOptions: [],
            laneOptions: [],
            uomOptions: [],
            poItemSubGroupOptions: [],
            statusOptions: [
                { text: 'Draft', value: 'draft' },
                { text: 'Pending', value: 'pending' },
                { text: 'Approved', value: 'approved' }
            ],
            // View params dialog
            viewParamsDialogActive: false,
            viewParamsData: {},
            activeTab: 0
        }
    },
    computed: {
        redoIcon: () => faRedo,
        settingsIcon: () => faCog,
        viewIcon: () => faEye,
        editIcon: () => faEdit,
        deleteIcon: () => faTrash,
        plusIcon: () => faPlus,
        isAdmin() {
            if (this.$store.state.organisation.organisation_user && this.$store.state.organisation.organisation_user.role) {
                let role = this.$store.state.organisation.organisation_user.role.role.toLowerCase();
                return ['admin', 'super admin', 'maintenance manager', 'site engineer'].includes(role);
            }
            return this.$store.state.auth.userRole == 'admin';
        },
        isActuallyAdmin() {
            if (this.$store.state.organisation.organisation_user && this.$store.state.organisation.organisation_user.role) {
                let role = this.$store.state.organisation.organisation_user.role.role.toLowerCase();
                return role == 'admin' || role == 'super admin';
            }
            return this.$store.state.auth.userRole == 'admin';
        },
        canApprove() {
            if (this.$store.state.organisation.organisation_user) {
                let role = this.$store.state.organisation.organisation_user.role.role.toLowerCase();
                return role == 'admin' || role == 'maintenance manager' || role == 'site engineer';
            }
            return false;
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0) return 1
            return Math.ceil(this.total_results / this.filters.limit)
        },
        poEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.purchase_order}?organisation=${orgId}` : this.$store.state.apiendpoints.purchase_order;
        },
        itemSubGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_sub_group}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_item_sub_group;
        },
        sideEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_side}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_side}?is_active=true`;
        },
        laneEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_lane}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_lane}?is_active=true`;
        },
        formattedSideOptions() {
            return (this.sideOptions || []).map(opt => ({ label: opt.code, value: opt.id }));
        },
        formattedLaneOptions() {
            return (this.laneOptions || []).map(opt => ({ label: opt.code, value: opt.id }));
        },
        has_active_filters() {
            return !!(this.filters.search || this.filters.filter_date || this.filters.filter_status);
        },
        hasDRPInputFormat() {
            if (!this.selectedItemSubGroup || !this.selectedItemSubGroup.drp_input_format) {
                return false;
            }
            return Object.keys(this.selectedItemSubGroup.drp_input_format).length > 0;
        },
        allDRPFieldsFilled() {
            if (this.drpKeys.length === 0) return true;
            return this.drpKeys.every(key => {
                const value = this.drpInputFormat[key];
                return value !== undefined && value !== null && value !== '';
            });
        },
        sortedDrpKeys() {
            return this.sortKeys(this.drpKeys);
        },
        sortedViewParamsKeys() {
            return this.sortKeys(Object.keys(this.viewParamsData));
        },
        isFormValid() {
            const requiredFieldsFilled = !!(
                this.formData.transaction_date &&
                this.formData.po_number &&
                this.formData.item_sub_group &&
                (this.formData.unit_rate !== null && this.formData.unit_rate !== undefined)
            );
            const drpFieldsValid = this.hasDRPInputFormat ? this.allDRPFieldsFilled : true;
            return requiredFieldsFilled && drpFieldsValid;
        }
    },
    watch: {
        'formData.po_number': {
            handler(newVal) {
                this.onPOChange(newVal);
            },
            immediate: false
        },
        'formData.item_sub_group': {
            handler(newVal) {
                this.onItemSubGroupChange(newVal);
            },
            immediate: false
        },
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;
                if (this.timer) clearTimeout(this.timer);
                this.timer = setTimeout(() => {
                    this.$router.push({
                        name: this.$route.name,
                        query: {
                            offset: newV.offset,
                            limit: newV.limit,
                            search: newV.search || undefined,
                            filter_date: newV.filter_date || undefined,
                            filter_status: newV.filter_status || undefined
                        }
                    });
                }, 800);
            }
        },
        "$route.query"() {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        },
        activeTab(newVal) {
            if (newVal === 0) {
                this.$nextTick(() => { this.loadData(); });
            }
        }
    },
    mounted() {
        this.loadSideOptions();
        this.loadLaneOptions();
        this.init();
    },
    methods: {
        loadSideOptions() {
            let url = this.sideEndpoint;
            Request(this, 'GET', url, {}).then(
                (res) => {
                    if (res && res.results) this.sideOptions = res.results;
                    else if (Array.isArray(res)) this.sideOptions = res;
                    else this.sideOptions = [];
                    this.normalizeDRPSelectValue('side');
                },
                () => {
                    this.sideOptions = [];
                    this.normalizeDRPSelectValue('side');
                }
            );
        },

        loadLaneOptions() {
            let url = this.laneEndpoint;
            Request(this, 'GET', url, {}).then(
                (res) => {
                    if (res && res.results) this.laneOptions = res.results;
                    else if (Array.isArray(res)) this.laneOptions = res;
                    else this.laneOptions = [];
                    this.normalizeDRPSelectValue('lane');
                },
                () => {
                    this.laneOptions = [];
                    this.normalizeDRPSelectValue('lane');
                }
            );
        },

        normalizeDRPSelectValue(key) {
            if (!this.drpInputFormat || this.drpInputFormat[key] === undefined || this.drpInputFormat[key] === null || this.drpInputFormat[key] === '') return;

            const options = key === 'side' ? (this.sideOptions || []) : (this.laneOptions || []);
            let rawValue = this.drpInputFormat[key];

            if (typeof rawValue === 'object' && rawValue !== null) {
                if (rawValue.id !== undefined && rawValue.id !== null) {
                    this.$set(this.drpInputFormat, key, rawValue.id);
                    return;
                }
                if (rawValue.code) {
                    rawValue = rawValue.code;
                }
            }

            if (typeof rawValue === 'string') {
                const trimmed = rawValue.trim();
                if (/^\d+$/.test(trimmed)) {
                    this.$set(this.drpInputFormat, key, parseInt(trimmed, 10));
                    return;
                }

                const matchByCode = options.find(opt => (opt.code || '').toLowerCase() === trimmed.toLowerCase());
                if (matchByCode) {
                    this.$set(this.drpInputFormat, key, matchByCode.id);
                }
                return;
            }

            if (typeof rawValue === 'number') {
                this.$set(this.drpInputFormat, key, rawValue);
            }
        },

        getItemSubGroupOption(source) {
            if (!source) return null;

            if (source.item_sub_group_detail && typeof source.item_sub_group_detail === 'object') {
                return source.item_sub_group_detail;
            }

            if (source.item_sub_group && typeof source.item_sub_group === 'object') {
                return source.item_sub_group;
            }

            if (source.id && source.code) {
                return source;
            }

            if (typeof source === 'number') {
                return { id: source, code: String(source) };
            }

            return null;
        },

        buildItemSubGroupOptions(details, preservedOptions = []) {
            const optionsById = {};

            preservedOptions.forEach(option => {
                if (option && option.id) {
                    optionsById[option.id] = option;
                }
            });

            details.forEach(detail => {
                const option = this.getItemSubGroupOption(detail) ||
                    (typeof detail.item_sub_group === 'number' ? { id: detail.item_sub_group, code: String(detail.item_sub_group) } : null);

                if (!option || !option.id) return;

                const existingOption = optionsById[option.id];
                if (!existingOption || (!existingOption.code && option.code) || existingOption.code === String(existingOption.id)) {
                    optionsById[option.id] = option;
                }
            });

            return Object.values(optionsById);
        },

        onPOChange(poId) {
            if (!this._editingMode) {
                // User manually changed PO — reset dependent fields
                this.formData.item_sub_group = null;
                this.poItemSubGroupOptions = [];
                this.selectedItemSubGroup = null;
                this.formData.uom_code = '';
            }
            if (poId) {
                this.selectedPO = { id: poId };
                this.fetchPODetails(poId);
                // Fetch unit rate from Purchase Order Detail
                this.fetchUnitRate(poId, this.formData.item_sub_group);
            } else {
                this.selectedPO = null;
                this.formData.unit_rate = null;
                this.poItemSubGroupOptions = [];
            }
        },

        fetchPODetails(poId) {
            if (!poId) return;
            let url = `${this.$store.state.apiendpoints.purchase_order_detail}?purchase_order=${poId}&limit=1000`;
            Request(this, 'GET', url, {}).then(
                (res) => {
                    let details = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        details = res.results;
                    } else if (Array.isArray(res)) {
                        details = res;
                    }

                    this.poItemSubGroupOptions = this.buildItemSubGroupOptions(details, this.poItemSubGroupOptions);
                },
                () => {
                    this.poItemSubGroupOptions = [];
                }
            );
        },

        fetchUnitRate(poId, subGroupId) {
            if (!poId || !subGroupId) {
                this.formData.unit_rate = null;
                return;
            }
            let url = `${this.$store.state.apiendpoints.purchase_order_detail}?purchase_order=${poId}&item_sub_group=${subGroupId}`;
            Request(this, 'GET', url, {}).then(
                (res) => {
                    let details = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        details = res.results;
                    } else if (Array.isArray(res)) {
                        details = res;
                    }

                    // Set unit rate from first detail (existing behaviour)
                    if (details.length > 0 && details[0].unit_rate) {
                        this.formData.unit_rate = parseFloat(details[0].unit_rate);
                    } else {
                        this.formData.unit_rate = null;
                    }

                    this.poItemSubGroupOptions = this.buildItemSubGroupOptions(details, this.poItemSubGroupOptions);
                },
                () => {
                    this.formData.unit_rate = null;
                    this.poItemSubGroupOptions = [];
                }
            );
        },

        onItemSubGroupChange(subGroupId) {
            if (subGroupId) {
                this.fetchUnitRate(this.formData.po_number, subGroupId);
                let url = `${this.$store.state.apiendpoints.master_item_sub_group}${subGroupId}/`;
                Request(this, 'GET', url, {}).then(
                    (res) => {
                        if (res) {
                            this.selectedItemSubGroup = res;

                            // Auto-fill UOM code
                            if (res.uom && typeof res.uom === 'object' && res.uom.code) {
                                this.formData.uom_code = res.uom.code;
                            } else if (res.uom && typeof res.uom === 'number') {
                                this.fetchUOMCode(res.uom);
                            } else if (res.uom_code) {
                                this.formData.uom_code = res.uom_code;
                            } else {
                                this.formData.uom_code = '';
                            }

                            // Set DRP Input Format keys
                            if (res.drp_input_format && typeof res.drp_input_format === 'object') {
                                this.drpKeys = Object.keys(res.drp_input_format);
                                // Don't wipe existing values (crucial for edit mode)
                                this.drpKeys.forEach(key => {
                                    if (this.drpInputFormat[key] === undefined || this.drpInputFormat[key] === null) {
                                        this.$set(this.drpInputFormat, key, '');
                                    }
                                });
                            } else {
                                this.drpKeys = [];
                                // Only reset if specifically no format found
                                if (Object.keys(this.drpInputFormat).length === 0) {
                                    this.drpInputFormat = {};
                                }
                            }
                        }
                    },
                    () => {}
                );
            } else {
                this.selectedItemSubGroup = null;
                this.formData.uom_code = '';
                this.drpKeys = [];
                this.drpInputFormat = {};
            }
        },

        sortKeys(keys) {
            const order = ['ch_from', 'ch_to', 'side', 'lane', 'qty', 'remark'];
            return [...keys].sort((a, b) => {
                let indexA = order.indexOf(a);
                let indexB = order.indexOf(b);
                if (indexA === -1) indexA = 999;
                if (indexB === -1) indexB = 999;
                if (indexA !== indexB) return indexA - indexB;
                return a.toLowerCase().localeCompare(b.toLowerCase());
            });
        },

        formatDrpValue(value) {
            if (value && typeof value === 'object') {
                return value.code || value.name || value.description || '-';
            }
            return value || '-';
        },

        fetchUOMCode(uomId) {
            let url = `${this.$store.state.apiendpoints.master_uom}${uomId}/`;
            Request(this, 'GET', url, {}).then(
                (res) => {
                    if (res && res.code) {
                        this.formData.uom_code = res.code;
                    }
                },
                () => {}
            );
        },

        openDRPDialog() {
            this.normalizeDRPSelectValue('side');
            this.normalizeDRPSelectValue('lane');
            this.drpDialogActive = true;
        },

        saveDRPInputFormat() {
            this.drpDialogActive = false;
            this.$vs.notify({
                time: 2000,
                title: 'Success',
                text: "DRP Input Format values saved!",
                iconPack: 'feather',
                icon: 'icon-check-circle',
                color: 'success'
            });
        },

        viewParams(item) {
            this.viewParamsData = item.params || {};
            this.viewParamsDialogActive = true;
        },

        getPoNumber(item) {
            if (item.po_number && typeof item.po_number === 'object') {
                return item.po_number.po_number || item.po_number.id || '-';
            }
            return item.po_number || '-';
        },

        getItemSubGroupCode(item) {
            // Use item_sub_group_detail from response
            if (item.item_sub_group_detail && typeof item.item_sub_group_detail === 'object') {
                return item.item_sub_group_detail.code || '-';
            }
            // Fallback to item_sub_group if it's an object
            if (item.item_sub_group && typeof item.item_sub_group === 'object') {
                return item.item_sub_group.code || '-';
            }
            return item.item_sub_group || '-';
        },

        getUOMCode(item) {
            // Direct uom object from response
            if (item.uom && typeof item.uom === 'object') {
                return item.uom.code || '-';
            }
            // Fallback to item_sub_group_detail
            if (item.item_sub_group_detail && item.item_sub_group_detail.uom) {
                if (typeof item.item_sub_group_detail.uom === 'object') {
                    return item.item_sub_group_detail.uom.code || '-';
                }
            }
            return item.uom_code || '-';
        },

        getStatusClass(status) {
            if (status === 'approved') return 'text-success font-semibold';
            if (status === 'pending') return 'text-warning font-semibold';
            return 'text-secondary';
        },

        loadData() {
            this.loading = true;
            this.$vs.loading();

            let url = this.$store.state.apiendpoints.drp_transaction;
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            };
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id;
            }
            if (this.filters.search) payload.search = this.filters.search;
            if (this.filters.filter_date) payload.transaction_date = this.filters.filter_date;
            if (this.filters.filter_status) payload.status = this.filters.filter_status;

            Request(this, 'GET', url, payload).then(
                (res) => {
                    if (res && res.data && Array.isArray(res.data)) {
                        this.results = res.data;
                        this.total_results = res.count !== undefined ? res.count : res.data.length;
                    } else if (res && res.results && Array.isArray(res.results)) {
                        this.results = res.results;
                        this.total_results = res.count || 0;
                    } else if (Array.isArray(res)) {
                        this.results = res;
                        this.total_results = res.length;
                    } else {
                        this.results = [];
                        this.total_results = 0;
                    }
                    this.calculate_page();
                    this.loading = false;
                    this.$vs.loading.close();
                },
                () => {
                    this.loading = false;
                    this.$vs.loading.close();
                    this.results = [];
                }
            );
        },

        save() {
            if (!this.isFormValid) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                });
                return;
            }

            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                });
                return;
            }

            this.submit_disabled = true;
            this.$vs.loading();

            let url = this.$store.state.apiendpoints.drp_transaction;
            let method = 'POST';

            if (this.editingId) {
                url += this.editingId + '/';
                method = 'PUT';
            }

            // Normalize side, lane and params to ensure IDs are sent, not objects
            let params = JSON.parse(JSON.stringify(this.drpInputFormat));
            if (params.side && typeof params.side === 'object') params.side = params.side.id;
            if (params.lane && typeof params.lane === 'object') params.lane = params.lane.id;

            let payload = {
                transaction_date: this.formData.transaction_date,
                po_number: parseInt(this.formData.po_number) || this.formData.po_number,
                item_sub_group: parseInt(this.formData.item_sub_group) || this.formData.item_sub_group,
                unit_rate: this.formData.unit_rate,
                side: params.side || null,
                lane: params.lane || null,
                remark: this.formData.remark,
                status: this.formData.status || 'draft',
                params: params,
                organisation: this.$store.state.organisation.organisation.id
            };

            Request(this, method, url, null, payload).then(
                () => {
                    this.submit_disabled = false;
                    this.$vs.loading.close();
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    });
                    this.resetForm();
                    this.loadData();
                    this.activeTab = 0;
                },
                (err) => {
                    this.submit_disabled = false;
                    this.$vs.loading.close();

                    let errorMsg = "Please fix the errors and try again!";
                    if (err.error) {
                        this.api_errors = err.error;
                        // Handle non_field_errors specifically
                        if (this.api_errors.non_field_errors && Array.isArray(this.api_errors.non_field_errors)) {
                            errorMsg = this.api_errors.non_field_errors.join(', ');
                        } else if (typeof this.api_errors === 'string') {
                            errorMsg = this.api_errors;
                        }

                        this.$vs.notify({
                            time: 4000,
                            title: 'Error',
                            text: errorMsg,
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        });
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        });
                    }
                }
            );
        },

        editItem(item) {
            this._editingMode = true; // Prevent onPOChange from resetting item_sub_group
            this.editingId = item.id;

            const poId = (item.po_number && typeof item.po_number === 'object') ? item.po_number.id : item.po_number;
            const subGroupId = (item.item_sub_group && typeof item.item_sub_group === 'object') ? item.item_sub_group.id : item.item_sub_group;
            const selectedSubGroupOption = this.getItemSubGroupOption(item);

            this.formData = {
                transaction_date: item.transaction_date,
                po_number: poId,
                item_sub_group: subGroupId,
                remark: item.remark || '',
                uom_code: this.getUOMCode(item),
                unit_rate: item.unit_rate || null,
                status: item.status || 'draft'
            };
            this.drpInputFormat = item.params ? JSON.parse(JSON.stringify(item.params)) : {};
            if (this.drpInputFormat.side === undefined || this.drpInputFormat.side === null || this.drpInputFormat.side === '') {
                this.drpInputFormat.side = item.side !== undefined ? item.side : this.drpInputFormat.side;
            }
            if (this.drpInputFormat.lane === undefined || this.drpInputFormat.lane === null || this.drpInputFormat.lane === '') {
                this.drpInputFormat.lane = item.lane !== undefined ? item.lane : this.drpInputFormat.lane;
            }
            this.normalizeDRPSelectValue('side');
            this.normalizeDRPSelectValue('lane');

            this.selectedItemSubGroup = selectedSubGroupOption;
            this.poItemSubGroupOptions = selectedSubGroupOption ? [selectedSubGroupOption] : [];

            // Load this PO's item sub group options for the dropdown
            if (poId) this.fetchPODetails(poId);

            this.$nextTick(() => { this._editingMode = false; });
            this.activeTab = 1;
        },
        openAddNew() {
            this.resetForm();
            this.activeTab = 1;
        },
        cancelEdit() {
            this.resetForm();
            this.activeTab = 0;
        },
        resetForm() {
            this.editingId = null;
            this.formData = {
                transaction_date: null,
                po_number: null,
                item_sub_group: null,
                uom_code: '',
                unit_rate: null,
                remark: '',
                status: 'draft'
            };
            this.drpInputFormat = {};
            this.selectedPO = null;
            this.selectedItemSubGroup = null;
            this.poItemSubGroupOptions = [];
            this.api_errors = {};
        },

        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },

        clearFilters() {
            this.filters.search = null;
            this.filters.filter_date = null;
            this.filters.filter_status = null;
            this.filters.offset = 0;
        },
        init() {
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.filters.search = this.$route.query.search || null;
            this.filters.filter_date = this.$route.query.filter_date || null;
            this.filters.filter_status = this.$route.query.filter_status || null;
            this.calculate_page();

            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({
                    name: this.$route.name,
                    query: {
                        ...this.$route.query,
                        offset: this.filters.offset,
                        limit: this.filters.limit
                    }
                });
            }

            this.loadData();
        },
        confirmDelete(item) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: 'Confirm Delete',
                text: `Are you sure you want to delete this DRP transaction dated ${item.transaction_date}? This action cannot be undone.`,
                acceptText: 'Delete',
                cancelText: 'Cancel',
                accept: () => this.deleteItem(item)
            });
        },
        deleteItem(item) {
            this.$vs.loading();
            let url = `${this.$store.state.apiendpoints.drp_transaction}${item.id}/`;
            Request(this, 'DELETE', url).then(res => {
                this.$vs.loading.close();
                this.$vs.notify({
                    title: 'Success',
                    text: 'DRP transaction deleted successfully',
                    color: 'success'
                });
                this.loadData();
            }).catch(err => {
                this.$vs.loading.close();
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to delete DRP transaction',
                    color: 'danger'
                });
                console.error(err);
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.mb-4 { margin-bottom: 1.5rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 1rem; }
.mt-2 { margin-top: 0.5rem; }
.mr-2 { margin-right: 0.5rem; }
.mr-1 { margin-right: 0.25rem; }
.mt-4 { margin-top: 1.5rem; }

.drp-container {
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #ebf0f3;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 5px;
}

.drp-table {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.drp-table-header {
    display: flex;
    background-color: #f8fafc;
    border-bottom: 2px solid #ebf0f3;
    position: sticky;
    top: 0;
    z-index: 10;
}

.drp-table-header .drp-cell {
    color: #475569;
    font-size: 0.85rem;
    padding: 14px 20px;
}

.drp-table-row {
    display: flex;
    border-bottom: 1px solid #ebf0f3;
    transition: background 0.2s ease;
    align-items: center;
    &:hover {
        background-color: #fcfdfe;
    }
    &:last-child { border-bottom: none; }
}

.drp-cell {
    padding: 10px 20px;
    display: flex;
    align-items: center;
    &.key-cell {
        width: 180px;
        min-width: 180px;
        background-color: #fcfdfe;
        border-right: 1px solid #ebf0f3;
    }
    &.value-cell {
        flex: 1;
        padding-left: 25px;
    }
}

.drp-key-text {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.85rem;
    text-transform: capitalize;
}

.drp-input {
    width: 100%;
    height: 38px;
    padding: 8px 15px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 0.9rem;
    background-color: #fff;
    color: #334155;
    transition: all 0.3s ease;
    outline: none;
    &:focus {
        border-color: #f97316;
        box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15);
    }
    &.disabled {
        background-color: #f1f5f9;
        color: #64748b;
        border-color: #e2e8f0;
        cursor: not-allowed;
    }
}

.v-select-drp {
    width: 100%;
    .vs__dropdown-toggle {
        border-radius: 8px !important;
        border: 1px solid #cbd5e1 !important;
        min-height: 38px !important;
        padding: 0 4px !important;
        background: #fff !important;
    }
    .vs__selected {
        margin: 5px 0 0 4px !important;
        color: #334155 !important;
    }
    .vs__actions {
        padding: 0 8px !important;
    }
}

.text-success { color: #28a745; }
.text-warning { color: #ffc107; }
.text-secondary { color: #6c757d; }
.font-semibold { font-weight: 600; }

.custom-label {
    display: block;
    font-size: 0.82rem;
    font-weight: 500;
    color: #626262;
    padding-left: 5px;
}

.align-button {
    height: 38px;
    margin-top: 5px !important;
}

::v-deep {
    .vs-input--input, .vs-select--input, .con-select, .vs-con-input {
        height: 38px !important;
        border-radius: 6px !important;
        border: 1px solid rgba(0, 0, 0, 0.15) !important;
    }

    .vs-input--label, .vs-select--label {
        display: none !important; /* Managed by custom-label */
    }

    .ls-select-foreign .vs-con-input,
    .ls-select-foreign .vs-input--input {
        height: 38px !important;
    }

    .vs-select--icon {
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
    }
}

.v-select-drp {
    background: #fff;
    .vs__dropdown-toggle {
        border-radius: 5px !important;
        padding: 0 !important;
        min-height: 38px !important;
        border: 1px solid rgba(0, 0, 0, 0.2) !important;
    }
    .vs__selected {
        margin: 5px 0 0 5px !important;
        padding: 0 !important;
    }
    .vs__search {
        margin: 5px 0 0 5px !important;
        padding: 0 !important;
    }
    .vs__actions {
        padding: 0 5px 0 0 !important;
    }
}
</style>
